import { Button } from '@op/opds.opux';
import { type FC, type ReactNode, useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import layoutStyles from './../../components/common/Layout.module.scss';
import { Feature } from '../../../types/featureSwitches';
import InsuranceEventsAlert from '../../components/InsuranceEventsAlert';
import { ProductDocuments } from '../../components/ProductDocuments';
import { type AuthenticatedInitialState } from '../../types/initialState';
import { getProductDocuments } from 'api/productDocuments';
import { type ProductDocument } from 'dto/productDocument';
import { ProfilingResult } from 'dto/ProfilingResult';
import giosg from 'giosg';
import i18n from 'i18n';
import { ActionButtonRow } from 'individual/components/ActionButtonRow';
import BeneficiarySelect from 'individual/components/BeneficiarySelect';
import EffectiveDateInfo from 'individual/components/EffectiveDateInfo';
import InfoSection from 'individual/components/InfoSection';
import PageHeading from 'individual/components/PageHeading/PageHeading';
import { PriceSummary } from 'individual/components/PriceSummary';
import PurchasePageIngress from 'individual/components/PurchasePageIngress';
import { useAppStore } from 'state/store';
import { getInitialState, isFeatureEnabled, sortProductDocuments } from 'utils';

interface Props {
  children: ReactNode;
}

const AuthenticatedPurchaseView: FC<Props> = ({ children }) => {
  const { marketableProduct, profilingResult, hasIncompleteInsuranceEvents } =
    getInitialState() as AuthenticatedInitialState;

  const handleAuthenticatedSubmit = useAppStore(
    (state) => state.handleAuthenticatedSubmit
  );
  const setProductDocumentsData = useAppStore(
    (state) => state.setProductDocumentsData
  );
  const { isConfirmingPurchase, productDocumentsData } = useAppStore(
    useShallow((state) => ({
      isConfirmingPurchase: state.isConfirmingPurchase,
      productDocumentsData: state.productDocumentsData,
    }))
  );

  const isProductDocumentsAvailable = useMemo(
    () =>
      marketableProduct?.documentTypes?.length ===
      (productDocumentsData?.length ?? 0),
    [productDocumentsData]
  );

  useEffect(() => {
    const getDocs = async () => {
      if (!marketableProduct) {
        return;
      }
      await getProductDocuments(marketableProduct.name).then((res) => {
        const documents = sortProductDocuments(res as ProductDocument[]);
        setProductDocumentsData(documents);
        return documents;
      });
    };
    void getDocs();
    giosg.hideGiosgChat();
    giosg.removeGiosgChatEntries();
  }, []);

  if (profilingResult === ProfilingResult.BLOCK) {
    return <></>;
  }

  return (
    <>
      <div className={layoutStyles.columnReverse}>
        <PageHeading
          data-testid='app-heading'
          title={i18n.t('authenticatedPurchase.buyInsuranceHeader')}
        />
        {isFeatureEnabled(
          Feature.FEATURE_SHOW_INCOMPLETE_EVENTS_NOTIFICATION
        ) &&
          hasIncompleteInsuranceEvents && <InsuranceEventsAlert />}
      </div>
      <PurchasePageIngress />
      {children}
      <BeneficiarySelect />
      <EffectiveDateInfo />
      <PriceSummary />
      {isProductDocumentsAvailable && (
        <ProductDocuments documentsData={productDocumentsData} />
      )}
      <div className={layoutStyles.columnReverse}>
        <InfoSection />
        <ActionButtonRow
          primaryButton={
            <Button
              finish
              onClick={handleAuthenticatedSubmit}
              loading={isConfirmingPurchase}
              data-testid='confirm-purchase-button'
              disabled={!isProductDocumentsAvailable}>
              {i18n.t('authenticatedPurchase.confirmPurchaseButton')}
            </Button>
          }
        />
      </div>
    </>
  );
};

export default AuthenticatedPurchaseView;
